/**
 * End-to-End Encryption Utilities for Messages
 * Uses Web Crypto API for client-side encryption
 * 
 * Key Generation: RSA-OAEP with 2048-bit keys
 * Message Encryption: AES-GCM for symmetric encryption
 * Hybrid approach: RSA encrypts AES key, AES encrypts message
 */

// Check if Web Crypto API is available
const crypto = window.crypto || window.msCrypto;
const subtle = crypto?.subtle;

if (!subtle) {
  console.warn('Web Crypto API not available. E2E encryption will use server fallback.');
}

/**
 * Generate RSA key pair for a user
 * Returns exportable public/private keys
 */
export async function generateKeyPair() {
  if (!subtle) {
    throw new Error('Web Crypto API not available');
  }

  const keyPair = await subtle.generateKey(
    {
      name: 'RSA-OAEP',
      modulusLength: 2048,
      publicExponent: new Uint8Array([1, 0, 1]),
      hash: 'SHA-256',
    },
    true, // extractable
    ['encrypt', 'decrypt']
  );

  // Export keys to JWK format for storage
  const publicKeyJwk = await subtle.exportKey('jwk', keyPair.publicKey);
  const privateKeyJwk = await subtle.exportKey('jwk', keyPair.privateKey);

  return {
    publicKey: publicKeyJwk,
    privateKey: privateKeyJwk,
    algorithm: 'RSA-OAEP-256',
  };
}

/**
 * Import a public key from JWK format
 */
export async function importPublicKey(jwk) {
  if (!subtle) throw new Error('Web Crypto API not available');

  return await subtle.importKey(
    'jwk',
    jwk,
    {
      name: 'RSA-OAEP',
      hash: 'SHA-256',
    },
    false,
    ['encrypt']
  );
}

/**
 * Import a private key from JWK format
 */
export async function importPrivateKey(jwk) {
  if (!subtle) throw new Error('Web Crypto API not available');

  return await subtle.importKey(
    'jwk',
    jwk,
    {
      name: 'RSA-OAEP',
      hash: 'SHA-256',
    },
    false,
    ['decrypt']
  );
}

/**
 * Generate a random AES key for message encryption
 */
async function generateAESKey() {
  return await subtle.generateKey(
    {
      name: 'AES-GCM',
      length: 256,
    },
    true,
    ['encrypt', 'decrypt']
  );
}

/**
 * Encrypt a message using hybrid encryption
 * 1. Generate random AES key
 * 2. Encrypt message with AES key
 * 3. Encrypt AES key with recipient's RSA public key
 * 
 * @param {string} plaintext - Message to encrypt
 * @param {CryptoKey|Object} recipientPublicKey - Recipient's public key (CryptoKey or JWK)
 * @returns {Object} Encrypted data package
 */
export async function encryptMessage(plaintext, recipientPublicKey) {
  if (!subtle) throw new Error('Web Crypto API not available');

  // Import public key if it's JWK
  let publicKey = recipientPublicKey;
  if (recipientPublicKey.kty) {
    publicKey = await importPublicKey(recipientPublicKey);
  }

  // Generate random AES key
  const aesKey = await generateAESKey();
  
  // Generate random IV
  const iv = crypto.getRandomValues(new Uint8Array(12));

  // Encrypt message with AES
  const encoder = new TextEncoder();
  const messageData = encoder.encode(plaintext);
  
  const encryptedMessage = await subtle.encrypt(
    {
      name: 'AES-GCM',
      iv: iv,
    },
    aesKey,
    messageData
  );

  // Export AES key for encryption
  const rawAesKey = await subtle.exportKey('raw', aesKey);

  // Encrypt AES key with RSA public key
  const encryptedAesKey = await subtle.encrypt(
    {
      name: 'RSA-OAEP',
    },
    publicKey,
    rawAesKey
  );

  // Return encrypted package
  return {
    encryptedMessage: arrayBufferToBase64(encryptedMessage),
    encryptedKey: arrayBufferToBase64(encryptedAesKey),
    iv: arrayBufferToBase64(iv),
    algorithm: 'RSA-OAEP+AES-GCM-256',
  };
}

/**
 * Decrypt a message using hybrid decryption
 * 1. Decrypt AES key with recipient's RSA private key
 * 2. Decrypt message with AES key
 * 
 * @param {Object} encryptedPackage - Encrypted data package
 * @param {CryptoKey|Object} privateKey - Recipient's private key
 * @returns {string} Decrypted plaintext
 */
export async function decryptMessage(encryptedPackage, privateKey) {
  if (!subtle) throw new Error('Web Crypto API not available');

  // Import private key if it's JWK
  let privKey = privateKey;
  if (privateKey.kty) {
    privKey = await importPrivateKey(privateKey);
  }

  // Decode from base64
  const encryptedAesKey = base64ToArrayBuffer(encryptedPackage.encryptedKey);
  const encryptedMessage = base64ToArrayBuffer(encryptedPackage.encryptedMessage);
  const iv = base64ToArrayBuffer(encryptedPackage.iv);

  // Decrypt AES key with RSA private key
  const rawAesKey = await subtle.decrypt(
    {
      name: 'RSA-OAEP',
    },
    privKey,
    encryptedAesKey
  );

  // Import AES key
  const aesKey = await subtle.importKey(
    'raw',
    rawAesKey,
    {
      name: 'AES-GCM',
      length: 256,
    },
    false,
    ['decrypt']
  );

  // Decrypt message with AES key
  const decryptedData = await subtle.decrypt(
    {
      name: 'AES-GCM',
      iv: new Uint8Array(iv),
    },
    aesKey,
    encryptedMessage
  );

  // Decode message
  const decoder = new TextDecoder();
  return decoder.decode(decryptedData);
}

// ==================== UTILITY FUNCTIONS ====================

/**
 * Convert ArrayBuffer to Base64 string
 */
function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/**
 * Convert Base64 string to ArrayBuffer
 */
function base64ToArrayBuffer(base64) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

// ==================== KEY STORAGE ====================

const KEY_STORAGE_NAME = 'socyy_e2e_keys';

/**
 * Store user's key pair in IndexedDB
 */
export async function storeKeyPair(userId, keyPair) {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(KEY_STORAGE_NAME, 1);
    
    request.onerror = () => reject(request.error);
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains('keys')) {
        db.createObjectStore('keys', { keyPath: 'userId' });
      }
    };
    
    request.onsuccess = () => {
      const db = request.result;
      const transaction = db.transaction(['keys'], 'readwrite');
      const store = transaction.objectStore('keys');
      
      store.put({
        userId,
        publicKey: keyPair.publicKey,
        privateKey: keyPair.privateKey,
        createdAt: new Date().toISOString(),
      });
      
      transaction.oncomplete = () => resolve(true);
      transaction.onerror = () => reject(transaction.error);
    };
  });
}

/**
 * Retrieve user's key pair from IndexedDB
 */
export async function getStoredKeyPair(userId) {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(KEY_STORAGE_NAME, 1);
    
    request.onerror = () => reject(request.error);
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains('keys')) {
        db.createObjectStore('keys', { keyPath: 'userId' });
      }
    };
    
    request.onsuccess = () => {
      const db = request.result;
      const transaction = db.transaction(['keys'], 'readonly');
      const store = transaction.objectStore('keys');
      const getRequest = store.get(userId);
      
      getRequest.onsuccess = () => resolve(getRequest.result || null);
      getRequest.onerror = () => reject(getRequest.error);
    };
  });
}

/**
 * Check if user has stored keys
 */
export async function hasStoredKeys(userId) {
  const keys = await getStoredKeyPair(userId);
  return keys !== null;
}

/**
 * Delete stored keys (for logout or key rotation)
 */
export async function deleteStoredKeys(userId) {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(KEY_STORAGE_NAME, 1);
    
    request.onerror = () => reject(request.error);
    
    request.onsuccess = () => {
      const db = request.result;
      const transaction = db.transaction(['keys'], 'readwrite');
      const store = transaction.objectStore('keys');
      store.delete(userId);
      
      transaction.oncomplete = () => resolve(true);
      transaction.onerror = () => reject(transaction.error);
    };
  });
}

// ==================== HIGH-LEVEL API ====================

/**
 * Initialize E2E encryption for a user
 * Generates keys if not already stored
 */
export async function initializeE2E(userId) {
  if (!subtle) {
    return {
      initialized: false,
      reason: 'Web Crypto API not available',
      fallback: 'server',
    };
  }

  try {
    // Check for existing keys
    let keys = await getStoredKeyPair(userId);
    
    if (!keys) {
      // Generate new keys
      const keyPair = await generateKeyPair();
      await storeKeyPair(userId, keyPair);
      keys = keyPair;
    }
    
    return {
      initialized: true,
      publicKey: keys.publicKey,
      hasPrivateKey: !!keys.privateKey,
      algorithm: 'RSA-OAEP+AES-GCM-256',
    };
  } catch (error) {
    console.error('E2E initialization error:', error);
    return {
      initialized: false,
      reason: error.message,
      fallback: 'server',
    };
  }
}

/**
 * Send an encrypted message
 * Encrypts for both sender (for sent folder) and recipient
 */
export async function sendEncryptedMessage(
  senderId,
  recipientPublicKey,
  plaintext
) {
  const senderKeys = await getStoredKeyPair(senderId);
  
  if (!senderKeys) {
    throw new Error('Sender keys not found. Initialize E2E first.');
  }
  
  // Encrypt for recipient
  const encryptedForRecipient = await encryptMessage(plaintext, recipientPublicKey);
  
  // Encrypt for sender (so they can read their sent messages)
  const encryptedForSender = await encryptMessage(plaintext, senderKeys.publicKey);
  
  return {
    forRecipient: encryptedForRecipient,
    forSender: encryptedForSender,
    timestamp: new Date().toISOString(),
  };
}

/**
 * Read an encrypted message
 */
export async function readEncryptedMessage(userId, encryptedPackage) {
  const keys = await getStoredKeyPair(userId);
  
  if (!keys || !keys.privateKey) {
    throw new Error('Private key not found. Cannot decrypt message.');
  }
  
  return await decryptMessage(encryptedPackage, keys.privateKey);
}

// Export crypto availability check
export const isE2EAvailable = !!subtle;
